import Phaser from "phaser";
import { LEVELS } from "../levels";
import { GameState } from "../state/GameState";
import { Palette } from "../ui/palette";
import { Tex } from "../ui/textures";
import { makeButton, starRow } from "../ui/widgets";
import { registerCharacterAnims } from "../ui/characterAnim";
import { registerLucasAnims } from "../ui/lucasAnim";
import { registerAndrewAnims } from "../ui/andrewAnim";

/** Each card's portrait = the character's real idle sprite, resting on frame 0 and
 *  playing its one-shot idle "fidget" on a slow, staggered cadence (below). */
interface IdleArt {
  atlas: string;
  rest: string; // the still pose (first idle frame)
  anim: string; // the one-shot fidget animation key
}
const IDLE_ART: Record<string, IdleArt> = {
  dan: { atlas: "char-dan", rest: "dan-idle-0", anim: "dan-idle" },
  lucas: { atlas: "char-lucas", rest: "lucas-idle-0", anim: "lucas-idle" },
  andrew: { atlas: "char-andrew", rest: "andrew-idle-0", anim: "andrew-idle" },
};

// Portraits fidget rarely and out of sync: one fidget per ~30s, staggered so the
// three never move together. Card i first fidgets at FIRST + i*STAGGER, then every
// PERIOD after. The frames themselves are ~1-2s, so the cards are still most of the time.
const IDLE_PERIOD_MS = 30_000;
const IDLE_STAGGER_MS = 9_000;
const IDLE_FIRST_MS = 4_000;
const ART_H = 96; // on-card portrait height (px), normalized across the atlases; big as the frame opening allows

/** Short one-line blurb shown under each portrait (the card no longer prints the
 *  character name; kept terse so the portrait can be large). Keyed by level id. */
const CARD_BLURB: Record<string, string> = {
  dan: "Level 1: Earn",
  lucas: "Level 2: Strategize",
  andrew: "Level 3: Fix",
};

/** The hub. Levels play in order; three stars on all three opens the finale. */
export class LevelSelectScene extends Phaser.Scene {
  constructor() {
    super("LevelSelect");
  }

  create() {
    const { width } = this.scale;

    // The idle anims live in the character modules; register them here so the
    // portraits can fidget without loading a level first (guards internally).
    registerCharacterAnims(this);
    registerLucasAnims(this);
    registerAndrewAnims(this);

    this.add
      .text(width / 2, 44, "LEVEL SELECT", {
        fontFamily: "monospace",
        fontSize: "34px",
        color: Palette.text,
      })
      .setOrigin(0.5);

    // Star counter: a small lit-star image (same art as the cards) in place of the ★
    // glyph, with the text, centered together as one group.
    const counterLabel = `${GameState.totalStars()} / 9    ·    Sheepies ${GameState.sheepyCount()} / 3`;
    const counterStar = this.add.image(0, 0, Tex.star).setOrigin(0, 0.5);
    counterStar.setScale(22 / counterStar.height); // 22px tall, aspect preserved
    const counterText = this.add
      .text(26, 0, counterLabel, {
        fontFamily: "monospace",
        fontSize: "15px",
        color: Palette.textDim,
      })
      .setOrigin(0, 0.5);
    const counterW = 26 + counterText.width;
    this.add.container(width / 2 - counterW / 2, 78, [counterStar, counterText]);

    // three level cards
    const cardW = 250;
    const gap = 30;
    const totalW = LEVELS.length * cardW + (LEVELS.length - 1) * gap;
    const startX = (width - totalW) / 2 + cardW / 2;
    const cardY = 250;

    LEVELS.forEach((lvl, i) => {
      const x = startX + i * (cardW + gap);
      const unlocked = GameState.isUnlocked(lvl.id);
      this.buildCard(x, cardY, cardW, CARD_BLURB[lvl.id] ?? lvl.subtitle, lvl.accent, unlocked, lvl.playerTex, {
        stars: GameState.stars(lvl.id),
        sheepy: GameState.hasSheepy(lvl.id),
        onPlay: () => this.scene.start(lvl.sceneKey),
        idle: IDLE_ART[lvl.id],
        idleIndex: i,
        // Andrew's idle art sits a touch left of centre in its frame; nudge it right.
        portraitDx: lvl.id === "andrew" ? 5 : 0,
      });
    });

    // The European market gate: win all three levels (9★) to unlock America,
    // which lives on the market screen. This just tells the player where they are.
    const europeWon = GameState.finaleUnlocked();
    this.add
      .text(
        width / 2,
        432,
        europeWon ? "★ Europe conquered - America is open on the market map." : "Earn 3★ on all three levels to conquer Europe.",
        { fontFamily: "monospace", fontSize: "15px", color: europeWon ? "#46d17f" : Palette.textDim },
      )
      .setOrigin(0.5);

    // Footer: back to the market map, and open the leaderboard (no need to finish
    // the game to view it).
    makeButton(this, width / 2 - 140, 480, "← Back to markets", () => this.scene.start("MarketSelect"), {
      width: 260,
      height: 38,
      fontSize: "15px",
    });
    makeButton(this, width / 2 + 140, 480, "🏆 Leaderboard", () => this.scene.start("Leaderboard"), {
      width: 260,
      height: 38,
      fontSize: "15px",
    });
  }

  private buildCard(
    x: number,
    y: number,
    w: number,
    subtitle: string,
    accent: number,
    unlocked: boolean,
    playerTex: string,
    data: { stars: number; sheepy: boolean; onPlay: () => void; idle?: IdleArt; idleIndex?: number; portraitDx?: number },
  ) {
    const h = 210;
    // Sci-fi metal frame (its dark navy centre is the card background). The plain
    // rect is gone; hover swaps to the lit frame and the whole frame is the click
    // target. Everything below sits inside the frame's opening (kept compact).
    const frame = this.add.image(x, y, Tex.hudBox);
    frame.setScale(w / frame.width, h / frame.height);
    if (!unlocked) frame.setTint(0x565b68); // dim + desaturate locked cards

    // Portrait (no name): large, filling the upper half of the opening.
    this.buildPortrait(x + (data.portraitDx ?? 0), y - 28, unlocked, playerTex, data.idle, data.idleIndex ?? 0);
    // One-line level blurb, well inside the opening (never under the side rails).
    this.add
      .text(x, y + 34, subtitle, {
        fontFamily: "monospace",
        fontSize: "13px",
        color: Palette.textDim,
        align: "center",
        wordWrap: { width: w - 70 },
      })
      .setOrigin(0.5);

    // Lower-centre of the opening: the three stars, then a gap, then the sheepy badge
    // (greyed when this level's sheepy is uncollected, rainbow once collected). Stars
    // and badge are centered together as one group.
    const stars = starRow(this, 0, 0, data.stars, 3, 0.8);
    const starsW = stars.getBounds().width;
    const sheepy = this.add.image(0, 0, data.sheepy ? Tex.sheepyHd : Tex.sheepyOff);
    sheepy.setScale(32 / sheepy.height); // ~32px tall, aspect preserved
    const badgeGap = 16;
    const groupW = starsW + badgeGap + sheepy.displayWidth;
    const left = x - groupW / 2;
    stars.setPosition(left + starsW / 2, y + 58);
    sheepy.setPosition(left + starsW + badgeGap + sheepy.displayWidth / 2, y + 58);

    if (unlocked) {
      frame.setInteractive({ useHandCursor: true });
      frame.on("pointerover", () => frame.setTexture(Tex.hudBoxHover));
      frame.on("pointerout", () => frame.setTexture(Tex.hudBox));
      frame.on("pointerdown", data.onPlay);
    } else {
      // Locked: a padlock over the (dimmed) portrait instead of an emoji label.
      const lock = this.add.image(x, y - 28, Tex.lock).setDepth(6);
      lock.setScale(64 / Math.max(lock.width, lock.height)); // fit ~64px, aspect preserved
    }
  }

  /** The card portrait: the character's real idle sprite resting on frame 0, with a
   *  slow staggered fidget (unlocked only). Falls back to the flat playerTex if the
   *  atlas/anim isn't available. */
  private buildPortrait(x: number, y: number, unlocked: boolean, playerTex: string, idle: IdleArt | undefined, index: number) {
    const alpha = unlocked ? 1 : 0.3;
    if (!idle || !this.textures.exists(idle.atlas)) {
      this.add.image(x, y, playerTex).setScale(2).setAlpha(alpha);
      return;
    }

    const s = this.add.sprite(x, y, idle.atlas, idle.rest).setAlpha(alpha);
    s.setScale(ART_H / s.height); // normalize the differing frame heights to one card size
    // After a fidget plays out, settle back to the still pose (so it reads as
    // "standing" between fidgets rather than freezing mid-gesture).
    s.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => s.setFrame(idle.rest));

    if (!unlocked || !this.anims.exists(idle.anim)) return; // locked cards stay still

    // First fidget staggered per card, then one every IDLE_PERIOD_MS. startAt shifts
    // the first fire earlier than a full period so the stagger lands where we want.
    const first = IDLE_FIRST_MS + index * IDLE_STAGGER_MS;
    this.time.addEvent({
      delay: IDLE_PERIOD_MS,
      startAt: IDLE_PERIOD_MS - first,
      loop: true,
      callback: () => s.play(idle.anim),
    });
  }
}
